import { type SubmitEvent as ReactSubmitEvent, type FormEvent, memo, useState } from "react";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { Card, type TabPane, SettingsLayout, Tabs } from "@clintonelec/react-storybook";
import "Components/ConfigLayout/Network/IpSetupSettings/IpSetupSettings.less";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectIpSettings, setIpSettingsAction } from "Data/Redux/Slices/Settings/Network/IpSettings";
import Ipv4SetupSettings from "Components/ConfigLayout/Network/IpSetupSettings/Ipv4SetupSettings";
import Ipv6SetupSettings from "Components/ConfigLayout/Network/IpSetupSettings/Ipv6SetupSettings";
import { produce } from "immer";
import { isEqual, merge } from "lodash";
import type { NetworkIpSettingsState } from "Interfaces";
import type { NetworkType } from "Data/Objects/Network";
import PortsSetupSettings from "Components/ConfigLayout/Network/IpSetupSettings/PortsSetupSettings";

export type IpSettingsFormFields = {
	ipv4NetworkType: HTMLFormElement;
	ipv4Address: HTMLFormElement;
	subnetMask: HTMLFormElement;
	ipv4Gateway: HTMLFormElement;
	dnsIpv4: HTMLFormElement[];
	ipv6NetworkType: HTMLFormElement;
	linkLocalAddress: HTMLFormElement;
	ipv6Address: HTMLFormElement;
	prefixLength: HTMLFormElement;
	ipv6Gateway: HTMLFormElement;
	dnsIpv6: HTMLFormElement[];
	httpPort: HTMLFormElement;
	rtspPort: HTMLFormElement;
	httpsPort: HTMLFormElement;
} & HTMLFormElement;

function IpSetupSettings() {
	const dispatch = useAppDispatch();
	const ipSetupSettings = useAppSelector(selectIpSettings);
	const { ipv4Settings, ipv6Settings, servicePortSettings } = ipSetupSettings;
	const [ ipv4NetworkType, setIpv4NetworkType ] = useState<NetworkType>(ipv4Settings.ipv4NetworkType);
	const [ ipv4Address, setIpv4Address ] = useState<string>(ipv4Settings.ipv4Address);
	const [ subnetMask, setSubnetMask ] = useState<string>(ipv4Settings.subnetMask);
	const [ ipv4Gateway, setIpv4Gateway ] = useState<string>(ipv4Settings.ipv4Gateway);
	const [ dnsIpv4, setDnsIpv4 ] = useState<string[]>(ipv4Settings.dnsIpv4);
	const [ ipv6NetworkType, setIpv6NetworkType ] = useState<NetworkType>(ipv6Settings.ipv6NetworkType);
	const [ linkLocalAddress, setLinkLocalAddress ] = useState<string>(ipv6Settings.linkLocalAddress);
	const [ ipv6Address, setIpv6Address ] = useState<string>(ipv6Settings.ipv6Address);
	const [ prefixLength, setPrefixLength ] = useState<number>(ipv6Settings.prefixLength);
	const [ ipv6Gateway, setIpv6Gateway ] = useState<string>(ipv6Settings.ipv6Gateway);
	const [ dnsIpv6, setDnsIpv6 ] = useState<string[]>(ipv6Settings.dnsIpv6);
	const [ httpPort, setHttpPort ] = useState<number>(servicePortSettings.httpPort);
	const [ rtspPort, setRtspPort ] = useState<number>(servicePortSettings.rtspPort);
	const [ httpsPort, setHttpsPort ] = useState<number>(servicePortSettings.httpsPort);
	const ipSetupSettingsDiff: Partial<NetworkIpSettingsState> = {
		ipv4Settings: {
			ipv4NetworkType,
			ipv4Address,
			subnetMask,
			ipv4Gateway,
			dnsIpv4
		},
		ipv6Settings: {
			ipv6NetworkType,
			linkLocalAddress,
			ipv6Address,
			prefixLength,
			ipv6Gateway,
			dnsIpv6
		},
		servicePortSettings: {
			httpPort,
			rtspPort,
			httpsPort
		}
	};

	const localIpSetupSettings = produce(ipSetupSettings, (draft) => {
		merge(draft, ipSetupSettingsDiff);
	});

	const setIpSetup = () => {
		dispatch(setIpSettingsAction(localIpSetupSettings));
	};

	const handleFormSubmit = (event: ReactSubmitEvent<IpSettingsFormFields>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setIpSetup();
			resetIpSetupSettingsDiff();
		}
	};

	const updateDnsIpv4 = (newDnsIpv4: string[]) => {
		setDnsIpv4(newDnsIpv4);
	};

	const updatePrefixLength = (newPrefixLength: string) => {
		setPrefixLength(Number(newPrefixLength));
	};

	const updateDnsIpv6 = (newDnsIpv6: string[]) => {
		setDnsIpv6(newDnsIpv6);
	};

	const updateHttpPort = (newHttpPort: string) => {
		setHttpPort(Number(newHttpPort));
	};

	const updateRtspPort = (newRtspPort: string) => {
		setRtspPort(Number(newRtspPort));
	};

	const updateHttpsPort = (newHttpsPort: string) => {
		setHttpsPort(Number(newHttpsPort));
	};

	const handleReset = (event: FormEvent<IpSettingsFormFields>) => {
		event.preventDefault();

		resetIpSetupSettingsDiff();
	};

	const resetIpSetupSettingsDiff = () => {
		setIpv4NetworkType(ipv4Settings.ipv4NetworkType);
		setIpv4Address(ipv4Settings.ipv4Address);
		setSubnetMask(ipv4Settings.subnetMask);
		setIpv4Gateway(ipv4Settings.ipv4Gateway);
		setDnsIpv4(ipv4Settings.dnsIpv4);
		setIpv6NetworkType(ipv6Settings.ipv6NetworkType);
		setLinkLocalAddress(ipv6Settings.linkLocalAddress);
		setIpv6Address(ipv6Settings.ipv6Address);
		setPrefixLength(ipv6Settings.prefixLength);
		setIpv6Gateway(ipv6Settings.ipv6Gateway);
		setDnsIpv6(ipv6Settings.dnsIpv6);
		setHttpPort(servicePortSettings.httpPort);
		setRtspPort(servicePortSettings.rtspPort);
		setHttpsPort(servicePortSettings.httpsPort);
	};

	const IpAddressPanes: TabPane[] = [
		{
			title: "IPv4",
			content: (
				<Ipv4SetupSettings
					ipSetupSettings={ localIpSetupSettings }
					updateIpv4NetworkType={ setIpv4NetworkType }
					updateIpv4Address={ setIpv4Address }
					updateSubnetMask={ setSubnetMask }
					updateIpv4Gateway={ setIpv4Gateway }
					updateDnsIpv4={ updateDnsIpv4 }
				/>
			)
		},
		{
			title: "IPv6",
			content: (
				<Ipv6SetupSettings
					ipSetupSettings={ localIpSetupSettings }
					updateIpv6NetworkType={ setIpv6NetworkType }
					updateLinkLocalAddress={ setLinkLocalAddress }
					updateIpv6Address={ setIpv6Address }
					updatePrefixLength={ updatePrefixLength }
					updateIpv6Gateway={ setIpv6Gateway }
					updateDnsIpv6={ updateDnsIpv6 }
				/>
			)
		},
		{
			title: "Ports",
			content: <PortsSetupSettings
				ipSetupSettings={ localIpSetupSettings }
				updateHttpPort={ updateHttpPort }
				updateHttpsPort={ updateHttpsPort }
				updateRtspPort={ updateRtspPort }
			/>
		}
	];

	return (
		<SettingsLayout.Form
			className="ip-setup-settings"
			noValidate
			onReset={ handleReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="medium" title="IP Setup">
					<Tabs panes={ IpAddressPanes } />
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ isEqual(localIpSetupSettings, ipSetupSettings) } />
		</SettingsLayout.Form>
	);
}

export default memo(IpSetupSettings);
